import { type ZodType } from 'zod';

import {
  bridgeDevicesSchema,
  bridgeStartSchema,
  isProvisionTerminal,
  provisionProgressSchema,
  type BridgeDevice,
  type ProvisionProgress,
  type ProvisionRequest,
} from '@/schemas/factory-usb';

/**
 * Client of the provisioning agent bridge.
 *
 * The bridge is a *local* service on the administrator's machine, not a part of the
 * operator API: plain `fetch` against loopback, `credentials: 'omit'`, no CSRF header.
 * Nothing from here goes through `apiFetch` (that one is for `/api` routes only).
 */

/** Loopback endpoint of the provisioning agent. */
export const BRIDGE_BASE_URL = 'http://127.0.0.1:8360';

const BRIDGE_TOKEN_HEADER = 'X-Bridge-Token';
const DEFAULT_POLL_INTERVAL_MS = 1_000;

/** Error codes the wizard maps to user-facing messages; never rendered raw. */
export const BRIDGE_ERROR_CODE = {
  /** Agent not running / port closed — the bridge is simply not there. */
  unavailable: 'BRIDGE_UNAVAILABLE',
  /** Http 409: the one-time token was already consumed — restart the wizard. */
  tokenConsumed: 'BRIDGE_TOKEN_CONSUMED',
  /** The bridge answered with an unexpected shape. */
  badResponse: 'BRIDGE_BAD_RESPONSE',
  /** Any other non-2xx from the bridge. */
  http: 'BRIDGE_HTTP_ERROR',
} as const;

/**
 * Shaped like `ApiError` from `@/lib/api` (`errorCode` / `detail` / `status`), so call sites
 * can handle operator-API and bridge failures the same way.
 */
export class FactoryBridgeError extends Error {
  readonly errorCode: string;
  readonly detail: string;
  /** `null` when the bridge was not reachable at all. */
  readonly status: number | null;

  constructor(message: string, errorCode: string, detail?: string, status?: number | null) {
    super(message);
    this.name = 'FactoryBridgeError';
    this.errorCode = errorCode;
    this.detail = detail ?? message;
    this.status = status ?? null;
  }
}

export function isFactoryBridgeError(error: unknown): error is FactoryBridgeError {
  return error instanceof FactoryBridgeError;
}

/** True for the abort raised by an `AbortController` we cancelled ourselves. */
export function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

interface BridgeRequestOptions {
  method?: 'GET' | 'POST';
  token?: string;
  body?: unknown;
  signal?: AbortSignal | undefined;
}

async function readJson(response: Response): Promise<unknown> {
  const text = await response.text().catch(() => '');
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

function toBridgeError(status: number, payload: unknown): FactoryBridgeError {
  const row = payload && typeof payload === 'object' ? (payload as Record<string, unknown>) : {};
  const message =
    typeof row.message === 'string' ? row.message : `Bridge request failed (${status})`;
  const detail = typeof row.detail === 'string' ? row.detail : message;

  if (status === 409) {
    return new FactoryBridgeError(message, BRIDGE_ERROR_CODE.tokenConsumed, detail, status);
  }

  const code =
    typeof row.code === 'string'
      ? row.code
      : typeof row.error_code === 'string'
        ? row.error_code
        : BRIDGE_ERROR_CODE.http;
  return new FactoryBridgeError(message, code, detail, status);
}

async function bridgeRequest(path: string, options: BridgeRequestOptions = {}): Promise<unknown> {
  const { method = 'GET', token, body, signal } = options;
  const headers: Record<string, string> = {};
  if (token) headers[BRIDGE_TOKEN_HEADER] = token;
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  const init: RequestInit = {
    method,
    headers,
    credentials: 'omit',
  };
  if (body !== undefined) {
    init.body = JSON.stringify(body);
  }
  if (signal !== undefined) {
    init.signal = signal;
  }

  let response: Response;
  try {
    response = await fetch(`${BRIDGE_BASE_URL}${path}`, init);
  } catch (error) {
    if (isAbortError(error)) throw error;
    throw new FactoryBridgeError(
      'Provisioning agent is not reachable',
      BRIDGE_ERROR_CODE.unavailable,
      error instanceof Error ? error.message : String(error),
      null
    );
  }

  const payload = await readJson(response);
  if (!response.ok) throw toBridgeError(response.status, payload);
  return payload;
}

function parsePayload<T>(schema: ZodType<T>, payload: unknown, what: string): T {
  const result = schema.safeParse(payload);
  if (!result.success) {
    throw new FactoryBridgeError(
      `Unexpected ${what} response from the provisioning agent`,
      BRIDGE_ERROR_CODE.badResponse,
      result.error.message,
      null
    );
  }
  return result.data;
}

/**
 * Opens a bridge run and returns its one-time token: the first call doubles as the
 * availability probe — if the agent is not running on this machine, this rejects.
 */
export async function startBridge(signal?: AbortSignal): Promise<string> {
  const payload = await bridgeRequest('/bridge/start', { signal });
  return parsePayload(bridgeStartSchema, payload, 'bridge start').token;
}

/** Connected media as the bridge sees them, including the ones it refuses to issue from. */
export async function listBridgeDevices(
  token: string,
  signal?: AbortSignal
): Promise<BridgeDevice[]> {
  const payload = await bridgeRequest('/devices', { token, signal });
  return parsePayload(bridgeDevicesSchema, payload, 'devices').devices;
}

/** Starts the issue. The token is one-time: a repeat on a consumed token answers 409. */
export async function startProvision(
  token: string,
  request: ProvisionRequest,
  signal?: AbortSignal
): Promise<ProvisionProgress> {
  const payload = await bridgeRequest('/provision', {
    method: 'POST',
    token,
    body: request,
    signal,
  });
  return parsePayload(provisionProgressSchema, payload, 'provision');
}

/** Current stage of a running issue. */
export async function getProvision(
  token: string,
  id: string,
  signal?: AbortSignal
): Promise<ProvisionProgress> {
  const payload = await bridgeRequest(`/provision/${encodeURIComponent(id)}`, { token, signal });
  return parsePayload(provisionProgressSchema, payload, 'provision status');
}

/** Hands a one-time authorization code to the already running agent (CONTRACT v0).
 * The response body is for the agent's own use — the front end never reads it. */
export async function authorizeBridge(
  token: string,
  code: string,
  signal?: AbortSignal
): Promise<void> {
  await bridgeRequest('/bridge/auth', {
    method: 'POST',
    token,
    body: { code },
    signal,
  });
}

function delay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    function onAbort(): void {
      clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    }
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * Polls `/provision/{id}` until `done` or `failed` and returns that terminal progress.
 * `onProgress` receives every intermediate stage, in order.
 */
export async function waitForProvision(
  token: string,
  id: string,
  onProgress?: (progress: ProvisionProgress) => void,
  signal?: AbortSignal,
  intervalMs: number = DEFAULT_POLL_INTERVAL_MS
): Promise<ProvisionProgress> {
  for (;;) {
    const progress = await getProvision(token, id, signal);
    onProgress?.(progress);
    if (isProvisionTerminal(progress.stage)) return progress;
    await delay(intervalMs, signal);
  }
}
